// A solar eclipse seen from inside the air: the sky in the Moon's shadow and
// the eclipsed Sun itself, drawn by the viewer's close sky dome
// (rocketry/sky.js, built with { eclipse: true } by earthclose.js).
//
// The sky. The dome integrates single scattering along each view ray; here
// the sunlight reaching every sample is cut by the fraction of the Sun's
// disc the Moon hides from that sample (eclSunHidden, src/eclipse/
// geometry.js), so the air inside the umbra goes dark and a ray that runs
// out of it toward the horizon picks up the light of the partly eclipsed air
// beyond, reddened by the long path back: the ring of sunset colour all
// round the horizon in totality. Light scattered more than once reaches the
// shadow from the sunlit air around it; uEclMs adds that as one more
// isotropic scattering of the light found uEclReach km off the axis, which
// is what keeps the zenith a deep blue rather than black.
//
// The Sun. While the Moon is near it the dome draws the disc instead of the
// scene's Sun meshes (main.js hides them): a limb-darkened photosphere, the
// Moon as a black silhouette whose edge carries a few kilometres of
// mountains and valleys (so the last of the Sun breaks into Baily's beads),
// the pink chromosphere and a few prominences just above the limb, and the
// white-light corona. The corona's fall-off is Baumbach's (1937) fit to
// eclipse photometry, B / B_disc = 1e-6 (0.0532 r^-2.5 + 1.425 r^-7 +
// 2.565 r^-17), shaped by streamers and polar plumes, and shown through
// uEclCoronaGamma, an eye's compression of its 200-fold drop between one
// and two solar radii. It is gated by how much of the Sun is still showing
// (uEclCorona from coronaLevel), since against even a thin crescent's glare
// it cannot be seen. The photosphere's cover is worked out exactly across
// each pixel an edge crosses, so a sliver far thinner than a pixel (a bead,
// or the last total eclipses, when the Moon is barely bigger than the Sun)
// still shows at its true brightness.

import { ECLIPSE_GLSL, discCover } from './geometry.js';
import { GIANT_SURFACE_GLSL } from '../redgiant.js';

export function createEclipseSkyUniforms() {
  return {
    uEclOn: { value: 0 },
    uEclSunKm: { value: [0, 0, 0] },
    uEclMoonKm: { value: [0, 0, 0] },
    uEclSunR: { value: 695700 },
    uEclMoonR: { value: 1737.4 },
    uEclAxisA: { value: [1, 0, 0] },
    uEclAxisB: { value: [0, 1, 0] },
    uEclReach: { value: 250 },
    uEclMs: { value: 0.03 },
    uEclDisc: { value: 0 },
    uEclSunDir: { value: [0, 1, 0] },
    uEclE1: { value: [1, 0, 0] },
    uEclE2: { value: [0, 0, 1] },
    uEclSunTan: { value: 0.00465 },
    uEclMoonXY: { value: [10, 10] },
    uEclMoonK: { value: 1 },
    uEclLimbK: { value: 0 },
    uEclLimbRot: { value: 0 },
    uEclPhoto: { value: 60 },
    uEclCorona: { value: 0 },
    uEclCoronaGamma: { value: 0.55 },
    uEclCoronaGain: { value: 0.06 },
    uEclChromo: { value: 0 },
    uEclPolePA: { value: 0.35 },
    uEclGlow: { value: 0 },
    uEclGlowXY: { value: [0, 0] },
    uEclTime: { value: 0 },
    uGiant: { value: 0 },
    uGiantS: { value: 0.5 },
    uGiantGain: { value: 3 },
    uGiantSky: { value: [1, 0.4, 0.16] },
  };
}

// Inserted into the dome's fragment shader (sky.js, SKY_ECLIPSE).
export const ECLIPSE_SKY_UNIFORMS_GLSL = /* glsl */ `
  uniform float uEclOn;       // 1 while the Moon's shadow is near enough to matter for the air
  uniform vec3 uEclSunKm;     // the Sun's centre, km from the planet's centre, the dome's axes
  uniform vec3 uEclMoonKm;    // the Moon's centre, the same
  uniform float uEclSunR;     // km
  uniform float uEclMoonR;    // km
  uniform vec3 uEclAxisA;     // two unit vectors square to the shadow axis
  uniform vec3 uEclAxisB;
  uniform float uEclReach;    // km off the axis of the farthest sunlit air the shadow is lit from
  uniform float uEclMs;       // its strength: about a thousandth of the daylight sky in a wide umbra
  uniform float uEclDisc;     // 1: the dome draws the Sun, the Moon and the corona
  uniform vec3 uEclSunDir;    // camera to the Sun's centre
  uniform vec3 uEclE1;        // the sky's axes at the Sun, square to uEclSunDir
  uniform vec3 uEclE2;
  uniform float uEclSunTan;   // tan of the Sun's angular radius
  uniform vec2 uEclMoonXY;    // the Moon's centre on those axes, in Sun radii
  uniform float uEclMoonK;    // the Moon's radius in Sun radii
  uniform float uEclLimbK;    // a kilometre of lunar relief in Sun radii
  uniform float uEclLimbRot;  // turns the Moon's limb profile with the Moon
  uniform float uEclPhoto;    // the photosphere's radiance at its centre
  uniform float uEclCorona;   // the corona's radiance at the limb (0 while the glare hides it)
  uniform float uEclCoronaGamma;
  uniform float uEclCoronaGain; // the corona's limb radiance against the dome's daylight sky
  uniform float uEclChromo;   // the chromosphere and prominences
  uniform float uEclPolePA;   // the Sun's north pole on the sky, radians from uEclE2
  uniform float uEclGlow;     // the last light's glare (the diamond ring), 0 above one per cent of the disc
  uniform vec2 uEclGlowXY;    // where that light is, in Sun radii
  uniform float uEclTime;     // seconds, for the slow change in the corona's rays
  uniform float uGiant;       // 1: the Sun is a red giant (src/redgiant.js), drawn by the dome
  uniform float uGiantS;      // its radius over its distance, the sine of its angular radius
  uniform float uGiantGain;   // its surface's radiance at the centre of the disc
  uniform vec3 uGiantSky;     // the sky's light scaled for the giant's (a red star, and far more of it)

${ECLIPSE_GLSL}
${GIANT_SURFACE_GLSL}

  float eclVisibleAt(vec3 pKm) {
    vec3 s = uEclSunKm - pKm;
    vec3 o = uEclMoonKm - pKm;
    float ds = length(s);
    float dO = length(o);
    vec3 sn = s / ds;
    vec3 on = o / dO;
    float c = dot(sn, on);
    if (c <= 0.0) return 1.0;
    float rs = asin(min(1.0, uEclSunR / ds));
    float ro = asin(min(1.0, uEclMoonR / dO));
    float d = atan(length(cross(sn, on)), c);
    return 1.0 - eclDiscCover(rs, ro, d);
  }

  float eclHash(float n) { return fract(sin(n * 12.9898) * 43758.5453); }

  // Lunar relief above the mean limb at position angle th, km: 40
  // harmonics from 40 to about 1,800 cycles round the limb (features 6 to
  // 270 km along it) with amplitudes falling slowly as n^-0.3, about 1 km
  // rms with peaks and valleys of 2 to 3 km, as the Kaguya limb profiles
  // used in eclipse predictions show. The valleys are where Baily's beads
  // shine.
  float eclLimb(float th) {
    float h = 0.0;
    float n = 40.0;
    for (int i = 0; i < 40; i++) {
      float ph = eclHash(float(i) + 1.7) * 6.2831853;
      float m = floor(n);
      h += sin(m * th + ph) * pow(m, -0.3);
      n *= 1.1;
    }
    return h * 1.1;
  }

  float eclNoise1(float x) {
    float i = floor(x);
    float f = fract(x);
    float u = f * f * (3.0 - 2.0 * f);
    return mix(eclHash(i), eclHash(i + 1.0), u);
  }

  vec3 eclPhotosphere(float r) {
    float mu = sqrt(max(0.0, 1.0 - r * r));
    float m = 1.0 - mu;
    float ld = 1.0 - 0.47 * m - 0.23 * m * m;
    return vec3(1.0, 0.95 - 0.08 * m, 0.88 - 0.22 * m) * ld;
  }

  // The white-light corona at r Sun radii (over 1), position angle th from
  // the Sun's north pole, in units of its radiance at the limb before the
  // display compression.
  float eclCorona(float r, float th) {
    float rr = max(r, 1.0);
    float base = (0.0532 * pow(rr, -2.5) + 1.425 * pow(rr, -7.0) + 2.565 * pow(rr, -17.0)) / 4.0422;
    float pole = abs(cos(th));                 // 1 over the poles, 0 over the equator
    // Fainter over the poles, as the corona of a declining cycle is.
    float shape = mix(1.0, 0.4, pole * pole);
    // Helmet streamers: broad over the limb, narrowing to stalks by two or
    // three radii, falling off more slowly than the corona round them.
    float st = 0.0;
    for (int i = 0; i < 7; i++) {
      float fi = float(i);
      float at = (eclHash(fi + 11.0) - 0.5) * 1.5 + (mod(fi, 2.0) < 0.5 ? 1.5707963 : -1.5707963);
      float w = (0.06 + 0.08 * eclHash(fi + 23.0)) * (0.35 + 1.3 / (rr * rr));
      float d = abs(mod(th - at + 3.14159265, 6.2831853) - 3.14159265);
      float a = 0.5 + 1.3 * eclHash(fi + 37.0);
      st += a * exp(-d * d / (w * w)) * smoothstep(1.0, 1.2, rr) * min(rr - 1.0, 1.6) * exp(-(rr - 1.0) / 3.0);
    }
    // Rays: fine structure across the limb that runs out radially, drifting slowly.
    float t = uEclTime * 0.002;
    float rays = 0.88 + 0.14 * eclNoise1(th * 55.0 + t) + 0.07 * eclNoise1(th * 160.0 - t * 1.7) - 0.1;
    // Polar plumes: fine bright rays over the poles.
    float plumes = pow(pole, 4.0) * pow(eclNoise1(th * 140.0 + 7.0), 3.0) * 0.8 * exp(-(rr - 1.0) / 0.8);
    return base * (shape * rays + st + plumes);
  }

  // The chromosphere's thin pink rim and a few prominences, outside the
  // limb, at r Sun radii and position angle th.
  float eclChromosphere(float r, float th) {
    float h = r - 1.0;
    if (h < 0.0 || h > 0.12) return 0.0;
    float spic = 0.0028 + 0.0016 * eclNoise1(th * 900.0) + 0.0008 * eclNoise1(th * 2700.0);
    float rim = smoothstep(spic, spic * 0.35, h);
    float prom = 0.0;
    for (int i = 0; i < 6; i++) {
      float fi = float(i);
      float at = eclHash(fi + 51.0) * 6.2831853;
      float w = 0.015 + 0.045 * eclHash(fi + 61.0);
      float tall = 0.018 + 0.05 * eclHash(fi + 71.0);
      float d = abs(mod(th - at + 3.14159265, 6.2831853) - 3.14159265);
      float across = exp(-d * d / (w * w));
      float wisp = 0.45 + 0.55 * eclNoise1(th * 600.0 + h * 900.0 + fi * 13.0);
      prom += across * smoothstep(tall, tall * 0.25, h) * wisp * (0.5 + 0.5 * eclHash(fi + 81.0));
    }
    return rim * 1.6 + prom * 0.7;
  }

  // The eclipsed Sun at X (the sky's position on the axes at the Sun, in Sun
  // radii, with fw its fwidth, both worked out at the top of main() where
  // derivatives are defined): rgb emission (premultiplied) and a, the share
  // of the pixel that is opaque (photosphere or Moon).
  vec4 eclipseDisc(vec2 X, vec2 fw, float cz) {
    if (cz <= 0.5) return vec4(0.0);
    float px = max(max(fw.x, fw.y), 1e-7);
    float r = length(X);
    if (r > 16.0) return vec4(0.0);
    vec2 dm = X - uEclMoonXY;
    float rho = length(dm);
    float limb = uEclMoonK + uEclLimbK * eclLimb(atan(dm.y, dm.x) + uEclLimbRot);
    // The photosphere and the Moon over the pixel. Where an edge runs
    // through it the pixel is cut into six lines along the radius from the
    // Sun's centre and the cover of each line is worked out exactly (the
    // Moon's limb meets the line at two points), so a bead or a last sliver
    // a tenth of a pixel wide keeps its true share of the light.
    vec3 photo = vec3(0.0);
    float opaque = 0.0;
    float reach = uEclLimbK * 4.0 + 2.0 * px;
    if (abs(r - 1.0) < 2.0 * px || abs(rho - uEclMoonK) < reach) {
      vec2 u = X / max(r, 1e-6);
      vec2 across = vec2(-u.y, u.x) * px;
      float h = 0.5 * px;
      for (int j = 0; j < 6; j++) {
        vec2 s = X + across * ((float(j) + 0.5) / 6.0 - 0.5);
        float rc = length(s);
        vec2 us = s / max(rc, 1e-6);
        float a = rc - h, b = rc + h;
        vec2 sm = s - uEclMoonXY;
        float L = uEclMoonK + uEclLimbK * eclLimb(atan(sm.y, sm.x) + uEclLimbRot);
        float um = dot(us, uEclMoonXY);
        float q = L * L - dot(uEclMoonXY, uEclMoonXY) + um * um;
        float m1 = 1e9, m2 = -1e9;
        if (q > 0.0) { q = sqrt(q); m1 = um - q; m2 = um + q; }
        float p0 = max(a, 0.0), p1 = min(b, 1.0);
        float lenP = max(p1 - p0, 0.0);
        float lenMP = max(min(p1, m2) - max(p0, m1), 0.0);
        float lenM = max(min(b, m2) - max(a, m1), 0.0);
        opaque += (lenP + lenM - lenMP) / (2.0 * h);
        photo += eclPhotosphere(clamp(rc, 0.0, 0.9995)) * (lenP - lenMP) / (2.0 * h);
      }
      photo /= 6.0;
      opaque = min(opaque / 6.0, 1.0);
    } else if (rho < limb) {
      opaque = 1.0;
    } else if (r < 1.0) {
      photo = eclPhotosphere(r);
      opaque = 1.0;
    }
    vec3 col = photo * uEclPhoto;
    // The diamond ring: the last light scattered in the air and the lens,
    // a sharp core and a wide wing round the beads, not the Sun's centre.
    if (uEclGlow > 0.0) {
      float g = length(X - uEclGlowXY);
      float q1 = g / 0.03, q2 = g / 0.35;
      col += vec3(1.0, 0.97, 0.92) * uEclGlow * (0.85 / (1.0 + q1 * q1) + 0.15 * pow(1.0 + q2 * q2, -1.5));
    }
    // Outside the Moon: the corona, the chromosphere and the prominences.
    float outside = smoothstep(limb - px, limb + px, rho);
    if (r > 1.0 && outside > 0.0) {
      float th = atan(X.x, X.y) - uEclPolePA;
      float b = eclCorona(r, th);
      vec3 corona = vec3(1.0, 0.975, 0.94) * pow(max(b, 0.0), uEclCoronaGamma) * uEclCorona * uEclCoronaGain;
      vec3 chromo = vec3(1.0, 0.16, 0.36) * eclChromosphere(r, th) * uEclChromo;
      col += (corona + chromo) * outside * (1.0 - opaque);
    }
    return vec4(col, opaque);
  }
`;

// Inside the dome's march (sky.js), for each sample p (metres from the
// planet's centre): the sunlight there cut by the eclipse, and the light the
// shadowed air gets from the sunlit air around it.
export const ECLIPSE_SKY_SAMPLE_GLSL = /* glsl */ `
      if (uEclOn > 0.5) {
        vec3 pk = p * 0.001;
        float vis = eclVisibleAt(pk);
        // The sunlit air round the shadow at three distances, nearer air
        // counting more: horizontal light is spent within about 85 km at
        // sea level, so a wide umbra (2027's is 260 km across) is much
        // darker inside than a narrow one.
        float around = 0.0;
        for (int k = 0; k < 3; k++) {
          float reach = uEclReach * (k == 0 ? 0.1 : (k == 1 ? 0.33 : 1.0));
          float w = k == 0 ? 0.5 : (k == 1 ? 0.3 : 0.2);
          around += w * 0.5 * (eclVisibleAt(pk + uEclAxisA * reach) + eclVisibleAt(pk - uEclAxisB * reach));
        }
        eclMs += exp(-extinction(odMid)) * d.x * max(around - vis, 0.0);
        att *= vis;
      }
`;

// The light that reaches each step of a ray drives the in-scatter; this adds
// the extra scattering into the shadow after the march.
export const ECLIPSE_SKY_AFTER_GLSL = /* glsl */ `
    color += uSunIntensity * uEclMs * eclMs * uBetaR;
`;

export const ECLIPSE_SKY_TOP_GLSL = /* glsl */ `
    vec3 eclDir = normalize(vDir);
    float eclCz = dot(eclDir, uEclSunDir);
    vec2 eclX = vec2(dot(eclDir, uEclE1), dot(eclDir, uEclE2)) / (max(eclCz, 1e-3) * uEclSunTan);
    vec2 eclFw = fwidth(eclX);
    vec3 eclMs = vec3(0.0);
    // The red giant's edge: where the line of sight meets its sphere (with
    // the star's distance as 1, the sphere's radius uGiantS).
    float giantCos = dot(eclDir, uEclSunDir);
    float giantDv = giantCos * giantCos - (1.0 - uGiantS * uGiantS);
    float giantFw = max(fwidth(giantDv), 1e-7);
`;

export const ECLIPSE_SKY_DISC_GLSL = /* glsl */ `
      if (uEclDisc > 0.5) {
        vec4 ed = eclipseDisc(eclX, eclFw, eclCz);
        color += ed.rgb * trans / max(uExposure, 1e-3);
        alpha = mix(alpha, 1.0, ed.a);
      }
      // The red giant: its surface where the line of sight meets the star,
      // in a frame fixed to the Sun's place in the sky, through the air.
      if (uGiant > 0.5) {
        color *= uGiantSky;
        float cover = giantCos > 0.0 ? smoothstep(-giantFw, giantFw, giantDv) : 0.0;
        if (cover > 0.0) {
          float sq = sqrt(max(giantDv, 0.0));
          vec3 nW = (eclDir * (giantCos - sq) - uEclSunDir) / uGiantS;
          vec3 nL = vec3(dot(nW, uEclE1), dot(nW, uEclE2), dot(nW, uEclSunDir));
          vec3 g = giantSurface(nL, sq / uGiantS, uEclTime) * uGiantGain;
          color += g * trans * cover / max(uExposure, 1e-3);
          alpha = mix(alpha, 1.0, cover);
        }
      }
`;

const sub = (a, b) => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const dot = (a, b) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const len = (a) => Math.sqrt(dot(a, a));
const scale = (a, k) => [a[0] * k, a[1] * k, a[2] * k];
const norm = (a) => scale(a, 1 / (len(a) || 1));
const cross = (a, b) => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];

// The fraction of the Sun's disc showing from p (km, any frame).
export function sunVisibleFrom(p, sunKm, moonKm, sunR, moonR) {
  const s = sub(sunKm, p);
  const o = sub(moonKm, p);
  if (dot(s, o) <= 0) return 1;
  const ds = len(s), dO = len(o);
  const rs = Math.asin(Math.min(1, sunR / ds));
  const ro = Math.asin(Math.min(1, moonR / dO));
  const d = Math.atan2(len(cross(s, o)), dot(s, o));
  return 1 - discCover(rs, ro, d);
}

// The last of the Sun is drawn brighter than the partial phases' disc: the
// disc is drawn at the viewer's usual Sun brightness, which the display
// holds at white, and that would leave a bead a tenth of a pixel across a
// dull speck beside the corona, which it outshines fifty times. So below
// one per cent of the disc the photosphere rises, up to 25 times at the
// beads and the diamond ring, and the bloom's firefly filter (main.js)
// stands aside for it.
export function eclipsePhotoBoost(visible) {
  return Math.min(25, Math.max(1, 0.01 / Math.max(visible, 1e-9)));
}

// How bright the corona is drawn for the share of the Sun still showing:
// none while a crescent's glare drowns it (from 0.3 per cent of the disc
// up), coming in through the last beads to full from a ten-thousandth down.
export function coronaLevel(visible) {
  const k = Math.min(1, Math.max(0, (Math.log10(Math.max(visible, 1e-12)) + 4) / 1.5));
  return 1 - k * k * (3 - 2 * k);
}

// Sets the uniforms for one frame. Every vector is in the dome's frame (the
// Earth's own axes), in km from the planet's centre, as plain arrays or
// {x, y, z}: the camera, the Sun's and the Moon's centres; the radii in km.
// Returns { on, disc, visible } (visible: the share of the Sun's disc
// showing from the camera).
// The Sun as a red giant over the dome (src/redgiant.js): cam and sun as
// for updateEclipseSky, sunR its radius in km. No eclipse is drawn then.
export function updateGiantSky(u, { cam, sun, sunR, time = 0 }) {
  const A = (v) => (Array.isArray(v) ? v : [v.x, v.y, v.z]);
  const c = A(cam), s = A(sun);
  const toSun = sub(s, c);
  const dS = len(toSun);
  const sunDir = scale(toSun, 1 / dS);
  const zen = norm(c);
  let e1 = cross(zen, sunDir);
  if (len(e1) < 1e-6) e1 = cross([1, 0, 0], sunDir);
  e1 = norm(e1);
  u.uEclOn.value = 0;
  u.uEclDisc.value = 0;
  u.uGiant.value = 1;
  u.uGiantS.value = Math.min(0.995, sunR / dS);
  u.uEclSunDir.value = sunDir;
  u.uEclE1.value = e1;
  u.uEclE2.value = cross(sunDir, e1);
  u.uEclTime.value = time;
}

export function updateEclipseSky(u, { cam, sun, moon, sunR, moonR, time = 0, corona = 1, chromo = 1, limbRot = 0 }) {
  const A = (v) => (Array.isArray(v) ? v : [v.x, v.y, v.z]);
  const c = A(cam), s = A(sun), m = A(moon);
  const toSun = sub(s, c), toMoon = sub(m, c);
  const dS = len(toSun), dM = len(toMoon);
  const sunDir = scale(toSun, 1 / dS), moonDir = scale(toMoon, 1 / dM);
  const rs = Math.asin(Math.min(1, sunR / dS));
  const rm = Math.asin(Math.min(1, moonR / dM));
  const sep = Math.atan2(len(cross(sunDir, moonDir)), dot(sunDir, moonDir));
  const near = dot(sunDir, moonDir) > 0 && sep < rs + rm + 3 * rs;
  const visible = near ? sunVisibleFrom(c, s, m, sunR, moonR) : 1;
  u.uGiant.value = 0;
  u.uEclOn.value = near ? 1 : 0;
  u.uEclDisc.value = near ? 1 : 0;
  if (!near) return { on: false, disc: false, visible };
  // Kept in km relative to the planet: the march's samples are within a few
  // thousand km of it, so float precision there is metres for the Moon and
  // about ten km for the Sun, which moves its direction by under 0.02".
  u.uEclSunKm.value = s;
  u.uEclMoonKm.value = m;
  u.uEclSunR.value = sunR;
  u.uEclMoonR.value = moonR;
  const axis = norm(sub(m, s));
  const ref = Math.abs(axis[2]) < 0.9 ? [0, 0, 1] : [1, 0, 0];
  const a = norm(cross(axis, ref));
  u.uEclAxisA.value = a;
  u.uEclAxisB.value = cross(axis, a);
  // The sky's axes at the Sun: E1 level, E2 up toward the zenith.
  const zen = norm(c);
  let e1 = cross(zen, sunDir);
  if (len(e1) < 1e-6) e1 = cross([1, 0, 0], sunDir);
  e1 = norm(e1);
  const e2 = cross(sunDir, e1);
  u.uEclSunDir.value = sunDir;
  u.uEclE1.value = e1;
  u.uEclE2.value = e2;
  const sunTan = Math.tan(rs);
  u.uEclSunTan.value = sunTan;
  const mz = dot(moonDir, sunDir);
  u.uEclMoonXY.value = [dot(moonDir, e1) / mz / sunTan, dot(moonDir, e2) / mz / sunTan];
  const moonK = Math.tan(rm) / sunTan;
  u.uEclMoonK.value = moonK;
  u.uEclLimbK.value = moonK / moonR;
  u.uEclLimbRot.value = limbRot;
  u.uEclPhoto.value = 60 * eclipsePhotoBoost(visible);
  // The last light's glare goes with the light left, centred on where it
  // is: on the limb opposite the Moon's centre in a total eclipse, at the
  // ring's centre (the disc less the Moon, by area) in an annular one. It
  // fades out above one per cent of the disc, where the crescent's own
  // brightness and the view's glare carry it.
  const fade = 1 - Math.min(1, Math.max(0, (Math.log10(Math.max(visible, 1e-12)) + 2.5) / 1));
  const [mx, my] = u.uEclMoonXY.value;
  const md = Math.hypot(mx, my) || 1;
  u.uEclGlowXY.value = moonK >= 1 ? [-mx / md, -my / md] : [-moonK * moonK * mx / (1 - moonK * moonK), -moonK * moonK * my / (1 - moonK * moonK)];
  u.uEclGlow.value = 400 * visible * fade * fade * (3 - 2 * fade);
  u.uEclCorona.value = coronaLevel(visible) * corona;
  u.uEclChromo.value = coronaLevel(visible * 0.5) * chromo;
  u.uEclTime.value = time;
  return { on: true, disc: true, visible };
}
